import React, {FC, useCallback, useEffect, useState} from 'react';
import {View} from 'react-native';
import {
  IChannelFragmentFragment,
  ILiveEventFragmentFragment,
  IVodItemFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import {usePaywallAccess} from '@src/Providers/PaywallProvider/PaywallProvider';
import type {IPaywallSetting} from '@src/Providers/PaywallProvider/PaywallProvider';
import {trackEvent} from '@src/Tracking/trackEvent';
import {TraceTokEvents} from '@src/Tracking/catalog';
import PlayerPaywallChoosePlan from './PlayerPaywallChoosePlan/PlayerPaywallChoosePlan';
import PlayerPaywallHome from './PlayerPaywallHome/PlayerPaywallHome';

const viewStyle = {
  flex: 1,
  alignItems: 'center' as const,
  width: '100%',
  height: '100%',
  maxWidth: '100%',
  position: 'relative' as 'relative',
};

type SupportedContentType =
  | IVodItemFragmentFragment
  | ILiveEventFragmentFragment
  | IChannelFragmentFragment;
interface PlayerPaywallProps {
  setting: IPaywallSetting;
  isLive: boolean;
  content: SupportedContentType | undefined;
  secondsWatched?: number;
  displayPaywall: boolean;
}

const getTrackingProps = (
  content: SupportedContentType,
  maxPreviewDurationMinutes: number,
  watchedTimeSeconds3Decimals: number,
) => {
  const {id, title, __typename} = content;

  const trackingProps = {
    id,
    title,
    __typename,
    maxPreviewDurationMinutes,
    watchedTimeSeconds3Decimals,
  };

  return trackingProps;
};

const PlayerPaywall: FC<PlayerPaywallProps> = ({
  setting,
  isLive,
  content,
  secondsWatched = -1,
  displayPaywall,
}) => {
  const [isChoosePlan, setIsChoosePlan] = useState(false);
  const [paywallTracked, setPaywallTracked] = useState(false);
  const {currentPaywall, previewDurationTv, previewDurationVod} = setting;
  const {
    isVodacomFreeUser,
    vodacomRedirectToPaywall,
    isMtnFreeUser,
    mtnRedirectToPaywall,
  } = usePaywallAccess();

  const maxPreview = isLive ? previewDurationTv : previewDurationVod;

  useEffect(() => {
    if (displayPaywall && isVodacomFreeUser) {
      vodacomRedirectToPaywall();
    }
  }, [displayPaywall, isVodacomFreeUser, vodacomRedirectToPaywall]);

  useEffect(() => {
    if (displayPaywall && isMtnFreeUser) {
      mtnRedirectToPaywall();
    }
  }, [displayPaywall, isMtnFreeUser, mtnRedirectToPaywall]);

  useEffect(function trackOnOpened() {
    if (!content) return;
    if (displayPaywall && !paywallTracked) {
      const trackingProps = getTrackingProps(content, maxPreview, secondsWatched);
      // paywall_viewed (canonical)
      trackEvent(TraceTokEvents.PaywallViewed, {
        content_id: trackingProps.content_id,
        paywall_context: 'player',
        placement: trackingProps.source_type,
      });
      setPaywallTracked(true);
    }

    if (!displayPaywall && paywallTracked) {
      setPaywallTracked(false);
    }
  }, [displayPaywall, content, maxPreview, secondsWatched, paywallTracked]);

  const handleChoosePlan = useCallback(() => {
    setIsChoosePlan(true);
    if (!content) return;

    const trackingProps = getTrackingProps(content, maxPreview, secondsWatched);
    // go_to_subscription (canonical)
    trackEvent(TraceTokEvents.GoToSubscription, {
      content_id: trackingProps.content_id,
      paywall_context: 'player',
    });
  }, [content, isLive, previewDurationTv, previewDurationVod]);

  if (isVodacomFreeUser || isMtnFreeUser) return null;

  const paywallContent = isChoosePlan ? (
    <View style={viewStyle}>
      <PlayerPaywallChoosePlan paywallId={currentPaywall?.id} />
    </View>
  ) : (
    <PlayerPaywallHome
      setting={setting}
      isLive={isLive}
      content={content}
      onChoosePlan={handleChoosePlan}
    />
  );

  return displayPaywall ? paywallContent : null;
};

export default PlayerPaywall;
